import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BrandingV2Interface, BrandingV2Service, UITheme } from '@galaxy/partner';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { Market } from './markets/market';
import { MarketsService } from './markets/markets.service';

// uiThemeToEnum converts legacy string theme to brandingV2 enum
export function uiThemeToEnum(uiTheme: string): UITheme {
  if (uiTheme === 'light') {
    return UITheme.UI_THEME_LIGHT;
  }
  return UITheme.UI_THEME_DARK;
}

// uiThemeFromEnum converts brandingV2 enum to legacy string theme
export function uiThemeFromEnum(uiTheme: UITheme): string {
  if (uiTheme === UITheme.UI_THEME_LIGHT) {
    return 'light';
  }
  return 'dark';
}

/*
 UserBrandingService contains the branding information for the current user based on the selectedMarket.
 This changes the logo and UiTheme shown in Partner Center depending on the market the user has access to, etc.
*/

@Injectable({ providedIn: 'root' })
export class UserBrandingService implements OnDestroy {
  private branding$$: BehaviorSubject<BrandingV2Interface> = new BehaviorSubject<BrandingV2Interface>(null);
  public branding$: Observable<BrandingV2Interface> = this.branding$$.asObservable().pipe(
    filter((b) => !!b),
    publishReplay(1),
    refCount(),
  );

  private readonly subscription: Subscription;

  constructor(
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
    private marketsService: MarketsService,
    private brandingV2Service: BrandingV2Service,
  ) {
    const selectedMarket$: Observable<Market | null> = this.marketsService.markets$
      .pipe(map((markets) => (markets.length === 1 ? markets[0] : null)))
      .pipe(distinctUntilChanged());

    this.subscription = combineLatest([this.partnerId$, selectedMarket$])
      .pipe(switchMap(([partnerId, market]) => this.brandingV2Service.getBranding(partnerId, market?.market_id)))
      .subscribe((branding) => this.branding$$.next(branding));
  }

  public setLogoUrl(logoUrl: string): void {
    if (logoUrl?.endsWith('=s58')) {
      // Uploading logo via AA returns with sizing config appended to url. Remove it as it makes it look small.
      logoUrl = logoUrl.replace('=s58', '');
    }
    this.branding$$.next({ ...this.branding$$.getValue(), logoUrl: logoUrl });
  }

  public setUiTheme(uiTheme: UITheme): void {
    this.branding$$.next({ ...this.branding$$.getValue(), uiTheme: uiTheme });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
